import { UIEvent, useEffect, useState } from 'react';
import Avatar from '../../juristec-ui/core/Avatar';
import IconButton from '../../juristec-ui/core/IconButton';
import Tooltip from '../../juristec-ui/core/Tooltip';
import Button from '../../juristec-ui/core/Button';
import MiniLoading from '../../juristec-ui/core/MiniLoading';

import { DashResume, KpiResume } from './types';

import { ArrowLeft, ArrowRight } from '../../juristec-ui/icons';

import { charts } from '../../options';
import { formatDateTime } from '../../juristec-ui/utils/functions/lab';
import assistantAvatar from '../../assets/temp.png';

import {
  MsgContainer, MsgRow, SubHeader, TimelineContainer, LoadingWrapper, Separator,
} from './styled/index.styled';

const typeMap = (type?: string) => {
  switch (type) {
    case 'Content':
      return 'Conteúdo';
    case 'smartLabel':
      return 'Smart Insight';
    default:
      // @ts-expect-error not typed yet
      return charts[type]?.label ?? 'KPI';
  }
};

const VSAvatar = () => (
  <Avatar
    style={{ pointerEvents: 'none' }}
    size="medium"
    name="Assistente"
    src={assistantAvatar || undefined}
  />
);

interface ResumesChatProps {
  dashResumes: Map<string, DashResume>;
  selected: DashResume['id'];
  deselect: () => void;
  kpis: Map<string, Record<string, string>>;
  showKpi: (timestamp: string, resume: KpiResume) => void;
}

const ResumesChat = ({
  dashResumes, selected, deselect, kpis, showKpi,
}: ResumesChatProps) => {
  const [scrollLock, setScrollLock] = useState<boolean>(false);
  const selectedRes = dashResumes.get(selected) ?? {
    createdAt: new Date(selected),
    resumes: new Map<string, KpiResume>(),
    status: 'doing',
  };

  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    setScrollLock(
      !((el.scrollTop + el.clientHeight) >= el.scrollHeight - 1),
    );
  };

  const scrollToTop = () => {
    const el = document.getElementById('timeline-resumes');
    if (el) el.scrollTop = 0;
  };

  useEffect(() => {
    if (scrollLock) return;
    const el = document.getElementById('timeline-resumes');
    if (el) el.scrollTop = el.scrollHeight;
  }, [selectedRes.status, selectedRes.resumes?.size]);

  return (
    <>
      <SubHeader>
        <Tooltip text="Voltar" direction="right">
          <IconButton
            variant="outlined"
            color="primary"
            size="medium"
            shape="rounded"
            onClick={deselect}
            style={{
              padding: '4px',
            }}
          >
            <ArrowLeft />
          </IconButton>
        </Tooltip>
        <h5>
          {formatDateTime(selectedRes.createdAt, { time: 'half' })}
        </h5>
      </SubHeader>
      <TimelineContainer id="timeline-resumes" onScroll={handleScroll}>
        {dashResumes !== null && selectedRes.resumes !== null && (
          <>
            {selectedRes.status === 'done' && (
              <>
                <MsgRow>
                  <VSAvatar />
                  <MsgContainer msgType="general">
                    <p>
                      {selectedRes.response}
                    </p>
                  </MsgContainer>
                </MsgRow>
                <Separator>
                  <h5>Resumos por KPI</h5>
                  <hr />
                </Separator>
              </>
            )}
            {Array.from(selectedRes.resumes).map(([key, kpiRes]) => (
              <MsgRow key={key}>
                <VSAvatar />
                <MsgContainer msgType="kpi" onClick={() => showKpi(selected, kpiRes)}>
                  <div>
                    <h5>
                      {`${typeMap(kpis.get(key)?.type)}: `}
                      {kpis.get(key)?.name ?? '-'}
                    </h5>
                  </div>
                  <p>
                    {kpiRes.response}
                  </p>
                  <div className="footer">
                    <span>Clique para exibir o KPI</span>
                    <ArrowRight />
                  </div>
                </MsgContainer>
              </MsgRow>
            ))}
          </>
        )}
        {selectedRes.status === 'doing' && (
          <MsgRow>
            <VSAvatar />
            <MsgContainer msgType="loading">
              <LoadingWrapper>
                <MiniLoading fill="primary" size="small" />
              </LoadingWrapper>
              <div className="footer">
                <span>
                  {`${selectedRes.resumes?.size || 0} / ${kpis.size + 1}`}
                </span>
              </div>
            </MsgContainer>
          </MsgRow>
        )}
        {selectedRes.status === 'error' && (
          <MsgRow>
            <VSAvatar />
            <MsgContainer msgType="error">
              <p>
                Algum erro aconteceu.
                Tente novamente mais tarde.
              </p>
            </MsgContainer>
          </MsgRow>
        )}
        {selectedRes.status === 'done' && (
          <MsgRow>
            <VSAvatar />
            <MsgContainer msgType="info">
              <Button
                variant="pattern"
                onClick={scrollToTop}
              >
                Resumo final
                <ArrowRight />
              </Button>
            </MsgContainer>
          </MsgRow>
        )}
      </TimelineContainer>
    </>
  );
};

export default ResumesChat;
